import axios from "axios"
import { BACKEND_URI } from "./appsettings"
import { PROJECT_MODES } from "./constants"
import { getDefaultSystemConfiguration } from "./utils"
import { ACTIVITY_TYPES } from './constants'
import { getScreenType } from './utils'

// Create axios instance with explicit HTTPS base URL
const api = axios.create({
	baseURL: BACKEND_URI,
	headers: { 
		'Content-Type': 'application/json'
	},
	withCredentials: true // if you need cookies/sessions
});

// Log the base URL during initialization (for debugging)
console.log('API Base URL:', BACKEND_URI);

export const addActivity = async (
	anonymousUserId,
	researchId,
	activityType,
	mapType,
	region,
	duration,
	noteContent,
	projectMode,
	screenType
) => {
	try {
		console.log('Sending activity:', {
			anonymousUserId,
			researchId,
			activityType,
			mapType,
			region,
			duration,
			noteContent,
			projectMode,
			screenType
		});

		const response = await api.post('/api/activities', {
			anonymousUserId,
			researchId,
			activityType,
			mapType,
			region,
			duration,
			noteContent,
			projectMode,
			screenType
		});
		return response.data;
	} catch (error) {
		console.error('Error adding activity:', error);
		throw error;
	}
};

export const getLatestNotesForUser = async (
	anonymousUserId
) => {
	try {
		const result = await api.post('/api/activities/getLatestNote', { anonymousUserId });
		const activity = result.data[0];
		return activity ? activity.noteContent : "";
	} catch (error) {
		console.error('Error getting notes:', error);
		return "";
	}
};

export const getProjectModeConfigForUser = async (emailId) => {
	try {
		const config = await api.get(
			`/api/projectModeConfig/${emailId}`
		);
		const projectMode = config.data[0]?.projectMode ?? 1;  // Default to 1 if undefined
		if (projectMode === 2) {
			return PROJECT_MODES.PLACEBO_MODE;
		}
		return PROJECT_MODES.INTERACTIVE_MAPS_MODE;
	} catch (error) {
		console.error('Error getting project mode:', error);
		return PROJECT_MODES.INTERACTIVE_MAPS_MODE;  // Default return value
	}
};

export const getSystemConfigByName = async (
	configurationName
) => {
	try{
		const config = await api.get(
			`/api/systemConfig/${configurationName}`
		)
		const sysConfig = config.data[0]
		if(sysConfig.configurationValueType === "NUMBER"){
			return Number(sysConfig.configurationValue)
		}
		return sysConfig.configurationValue
	}
	catch(e){
		console.log(`Error occurred, hence proceeding with default value for ${configurationName}. Please check if the value for ${configurationName} is set in the System Configrutions table in the database. Error: ${e}`)
		return getDefaultSystemConfiguration(configurationName)
	}
}

export const getLatestCanvasState = async (anonymousUserId) => {
	try {
		const response = await api.get(
			`/api/activities/latest-canvas-state/${anonymousUserId}`
		);
		return response.data;
	} catch (error) {
		console.error('Error fetching canvas state:', error);
		return null;
	}
};

export const getLatestActivity = async (anonymousUserId, activityType) => {
	try {
		const response = await api.get(
			`/api/activities/latest/${anonymousUserId}/${activityType}`
		);
		return response.data;
	} catch (error) {
		console.error(`Error fetching ${activityType} activity:`, error);
		return null;
	}
};

export const saveCanvasStateInChunks = async (anonymousUserId, researchId, data, projectMode) => {
	try {
		const screenType = getScreenType();

		// Compress image function
		const compressImage = (dataUrl) => {
			if (!dataUrl) return null;
			
			return new Promise((resolve) => {
				// Create an image to load the data URL
				const img = new Image();
				img.onload = () => {
					// Create a temporary canvas for resizing
					const tempCanvas = document.createElement('canvas');
					const tempCtx = tempCanvas.getContext('2d');
					
					// Set max dimensions (e.g., 800px width)
					const maxWidth = 800;
					const scale = maxWidth / img.width;
					
					tempCanvas.width = maxWidth;
					tempCanvas.height = img.height * scale;
					
					// Draw scaled image
					tempCtx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height);
					
					// Return highly compressed JPEG
					resolve(tempCanvas.toDataURL('image/jpeg', 0.3));
				};
				img.src = dataUrl;
			});
		};

		// First save the canvas content and metadata
		const metadataPayload = {
			leftCanvasContent: data.leftCanvasContent,
			rightCanvasContent: data.rightCanvasContent,
			hasImages: true
		};

		// Save metadata first
		await addActivity(
			anonymousUserId,
			researchId,
			ACTIVITY_TYPES.SAVE_CANVAS,
			null,
			JSON.stringify(metadataPayload),
			null,
			null,
			projectMode,
			screenType
		);

		// Then save each image separately with compression
		if (data.leftImage) {
			const compressedLeftImage = await compressImage(data.leftImage);
			await addActivity(
				anonymousUserId,
				researchId,
				ACTIVITY_TYPES.SAVE_LEFT_IMAGE,
				null,
				JSON.stringify({ image: compressedLeftImage }),
				null,
				null,
				projectMode,
				screenType
			);
		}

		if (data.rightImage) {
			const compressedRightImage = await compressImage(data.rightImage);
			await addActivity(
				anonymousUserId,
				researchId,
				ACTIVITY_TYPES.SAVE_RIGHT_IMAGE,
				null,
				JSON.stringify({ image: compressedRightImage }),
				null,
				null,
				projectMode,
				screenType
			);
		}

		return true;
	} catch (error) {
		console.error('Error saving canvas state:', error);
		throw error;
	}
};

export const getCompleteCanvasState = async (anonymousUserId) => {
	try {
		// Get the main canvas state
		const mainState = await getLatestCanvasState(anonymousUserId);
		if (!mainState || !mainState.hasImages) return mainState;

		// Get the images
		const leftImage = await getLatestActivity(anonymousUserId, ACTIVITY_TYPES.SAVE_LEFT_IMAGE);
		const rightImage = await getLatestActivity(anonymousUserId, ACTIVITY_TYPES.SAVE_RIGHT_IMAGE);

		return {
			...mainState,
			leftImage: leftImage?.region?.image,
			rightImage: rightImage?.region?.image
		};
	} catch (error) {
		console.error('Error loading complete canvas state:', error);
		throw error;
	}
};

export const clearSavedState = async (anonymousUserId) => {
	try {
		const response = await api.delete(
			`/api/activities/clear-state/${anonymousUserId}`
		);
		console.log('Clear state response:', response);
		return true;
	} catch (error) {
		console.error('Error clearing saved state:', error);
		throw error;
	}
};